import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { authAPI } from '../services/api';
import AuthBrand, { useAppBrand } from '../components/AuthBrand';
import ErrorBoundary from '../components/ErrorBoundary';
import { formatApiError } from '../utils/errorMessage';
import { clearSavedBusiness } from '../utils/businessSelection';

// The reset route also answers 401 for a wrong 2FA code, so the expired-link
// case is recognised by the server's message, not the status alone.
const LINK_EXPIRED = 'This link has expired. Request a new one.';

const ResetPassword = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  // Read once, then kept only in state: the token must not stay in the address
  // bar, where it would ride along as a Referer or document.referrer.
  const [token] = useState(() => searchParams.get('token') || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [code, setCode] = useState('');
  const [requires2FA, setRequires2FA] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [linkExpired, setLinkExpired] = useState(false);
  const [success, setSuccess] = useState(false);
  const appConfig = useAppBrand();
  const brandColor = appConfig?.primary_color || '#3B82F6';

  // Declared before the precheck effect so the URL is clean before the precheck is sent.
  useEffect(() => {
    if (searchParams.has('token')) {
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!token) return;
    authAPI.resetPasswordRequires2FA(token)
      .then((res) => setRequires2FA(Boolean(res.data.requires2FA)))
      .catch(() => {
        // If the token is already invalid/expired, let the real submit
        // surface that error instead of failing silently here.
      });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLinkExpired(false);

    if (!token) {
      setError("For your security, this page can't be reloaded. Open the reset link from your email again, or request a new one.");
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (requires2FA && !code) {
      setError('Enter your authenticator app code or a recovery code to continue.');
      return;
    }

    setLoading(true);
    try {
      await authAPI.resetPassword(token, password, code || undefined);
      setSuccess(true);
      window.setTimeout(() => {
        try {
          sessionStorage.removeItem('token');
          sessionStorage.removeItem('refreshToken');
          sessionStorage.removeItem('user');
          clearSavedBusiness();
          sessionStorage.removeItem('formationDateEdits');
        } catch (storageError) {
          // Sign-in still continues if the browser refuses storage.
        }
        window.location.assign('/login');
      }, 1500);
    } catch (err) {
      if (err.response?.status === 401 && err.response?.data?.error === LINK_EXPIRED) {
        setLinkExpired(true);
      }
      setError(formatApiError(err, 'This reset link is invalid or has expired. Please request a new one.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ErrorBoundary section="Reset Password">
      <div className="min-h-screen bg-gray-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <AuthBrand appConfig={appConfig} />
            <h2 className="mt-2 text-center text-2xl font-bold text-gray-900">Set a new password</h2>
          </div>

          <div className="bg-white p-8 shadow rounded-lg">
            {success ? (
              <div className="text-center space-y-4">
                <div className="rounded-md bg-green-50 p-4">
                  <p className="text-sm text-green-700">
                    Password reset successful. Redirecting to sign in...
                  </p>
                </div>
              </div>
            ) : (
              <form className="space-y-6" onSubmit={handleSubmit}>
                {error && (
                  <div className="rounded-md bg-red-50 p-4" role="alert">
                    <div className="text-sm text-red-700">
                      {linkExpired ? (
                        <>
                          This link has expired.{' '}
                          <Link to="/forgot-password" className="underline">Request a new one</Link>.
                        </>
                      ) : error}
                    </div>
                  </div>
                )}
                {!token && (
                  <div className="rounded-md bg-yellow-50 p-4">
                    <p className="text-sm text-yellow-700">
                      For your security, this page can&apos;t be reloaded. Open the reset link from your email again, or{' '}
                      <Link to="/forgot-password" className="underline">request a new one</Link>.
                    </p>
                  </div>
                )}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    New password
                  </label>
                  <input
                    id="password"
                    type="password"
                    required
                    minLength={8}
                    className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="At least 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                    Confirm new password
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    required
                    className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Re-enter your new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </div>
                {requires2FA && (
                  <div>
                    <label htmlFor="code" className="block text-sm font-medium text-gray-700">
                      Authenticator app code or recovery code
                    </label>
                    <p className="text-xs text-gray-500 mb-1">
                      This account has two-factor authentication enabled. Enter a code from
                      your authenticator app, or one of your recovery codes.
                    </p>
                    <input
                      id="code"
                      type="text"
                      required
                      className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="6-digit code or recovery code"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                    />
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:bg-gray-400"
                  style={{ backgroundColor: loading ? undefined : brandColor }}
                >
                  {loading ? 'Resetting...' : 'Reset password'}
                </button>
                <div className="text-center">
                  <Link to="/login" className="text-sm hover:underline" style={{ color: brandColor }}>
                    Back to sign in
                  </Link>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default ResetPassword;
